import { createContext, useContext, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { ChevronDown, ChevronRight, Info, Languages, Menu, PanelLeftClose, PanelLeftOpen, X, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { microcopy } from "../lib/microcopy";
import { Emblem } from "./emblem";
import { Button } from "./button";
import { Tooltip } from "./overlays";

// ---------------------------------------------------------------- Federal gov bar
export interface GovBarProps {
  ministryName: string;
  ministryNameSecondary: string;
  platformName: string;
  locale: "en" | "ar";
  onToggleLocale: () => void;
  right?: ReactNode;
  /**
   * The grouped header of the UAE Design System: identity, then `search`, then the user's tools (`right`, with the language switch among
   * them), then `account` (the account menu, which holds sign-out). Without `account` the bar keeps its earlier layout.
   */
  search?: ReactNode;
  account?: ReactNode;
  /** Official ministry logotype (bilingual PNG/SVG). When provided it replaces the emblem + wordmark. */
  logo?: { src: string; alt: string };
  homeHref?: string;
  onHome?: () => void;
  className?: string;
}

/** The narrow-screen menu of an AppShell: the header shows its button, where the navigation is looked for, instead of a floating one. */
const NavDrawerContext = createContext<{ open: boolean; toggle: () => void } | null>(null);

export function GovBar({ ministryName, ministryNameSecondary, platformName, locale, onToggleLocale, right, search, account, logo, homeHref = "/", onHome, className }: GovBarProps) {
  const drawer = useContext(NavDrawerContext);
  const language = (
    <Button variant={account ? "ghost" : "outline"} size="sm" onClick={onToggleLocale} className="gap-1.5 font-medium" aria-label={microcopy("switchLanguage")}>
      <Languages className="size-4" />
      <span lang={locale === "ar" ? "en" : "ar"} className={locale === "ar" ? "font-sans" : "font-arabic"}>{locale === "ar" ? "English" : "العربية"}</span>
    </Button>
  );
  return (
    <header data-print="hide" className={cn("sticky top-0 z-40 border-b border-sand-200/80 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70", className)}>
      <div className="h-1 w-full bg-gradient-to-r from-aegold-700 via-aegold-400 to-aegold-700" />
      <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-4 px-4 sm:px-6">
        {drawer ? (
          <Button variant="ghost" size="icon-sm" className="-me-2 lg:hidden" onClick={drawer.toggle} aria-expanded={drawer.open} aria-label={microcopy("toggleNavigation")}>
            {drawer.open ? <X /> : <Menu />}
          </Button>
        ) : null}
        <a
          href={homeHref}
          onClick={(e) => {
            if (onHome) {
              e.preventDefault();
              onHome();
            }
          }}
          className="flex items-center gap-3 min-w-0"
        >
          {logo ? (
            <img src={logo.src} alt={logo.alt} className="h-10 w-auto shrink-0 sm:h-11" />
          ) : (
            <>
              <Emblem size={40} />
              <div className="hidden min-w-0 flex-col leading-tight sm:flex">
                <span className="truncate text-[13px] font-semibold tracking-wide text-aegold-800 uppercase">{ministryName}</span>
                <span className="truncate text-[11px] text-sand-700">{ministryNameSecondary}</span>
              </div>
            </>
          )}
          {logo ? null : (
            <>
              <span className="mx-1 hidden h-8 w-px bg-sand-300 xl:block" />
              <span className="hidden truncate font-heading text-base font-semibold text-aeblack-950 xl:block">{platformName}</span>
            </>
          )}
        </a>
        {account ? (
          <>
            {search ? <div className="flex min-w-0 flex-1 justify-start ps-2 lg:ps-6">{search}</div> : <div className="flex-1" />}
            <div className="flex shrink-0 items-center gap-1">
              {right}
              {language}
            </div>
            <span className="hidden h-6 w-px bg-sand-200 sm:block" aria-hidden />
            <div className="shrink-0">{account}</div>
          </>
        ) : (
          <div className="ms-auto flex items-center gap-2">
            {right}
            {language}
          </div>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------- Side navigation
export interface NavItem {
  key: string;
  label: ReactNode;
  icon: LucideIcon;
  href: string;
  badge?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}

export interface NavSection {
  title?: ReactNode;
  items: NavItem[];
}

export function SideNav({ sections, footer, className, collapsed = false, onToggleCollapsed }: { sections: NavSection[]; footer?: ReactNode; className?: string; collapsed?: boolean; onToggleCollapsed?: () => void }) {
  const toggleLabel = microcopy(collapsed ? "expandSidebar" : "collapseSidebar");
  // Tooltips open away from the rail: to the right in LTR, to the left when the document runs right-to-left.
  const tipSide = typeof document !== "undefined" && document.documentElement.dir === "rtl" ? "left" : "right";
  return (
    <nav className={cn("flex h-full flex-col gap-4 overflow-y-auto scrollbar-thin py-4", className)} aria-label={microcopy("mainMenu")}>
      {sections.map((section, si) => (
        <div key={si} className="px-3">
          {collapsed ? (
            si > 0 ? <div className="mx-3 h-px bg-sand-200" /> : null
          ) : section.title ? (
            <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-widest text-sand-600">{section.title}</p>
          ) : null}
          <ul className="flex flex-col gap-0.5">
            {section.items.map((item) => {
              const link = (
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                  aria-current={item.active ? "page" : undefined}
                  className={cn(
                    "group relative flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200",
                    collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2",
                    item.active ? "text-aegold-900" : "text-aeblack-800 hover:bg-sand-100 hover:text-aeblack-950",
                  )}
                >
                  {item.active ? <motion.span layoutId="nav-active" className="absolute inset-0 -z-10 rounded-lg bg-aegold-50 ring-1 ring-aegold-200" transition={{ type: "spring", stiffness: 500, damping: 40 }} /> : null}
                  <item.icon className={cn("size-[18px] shrink-0 transition-colors", item.active ? "text-aegold-700" : "text-sand-600 group-hover:text-aeblack-900")} />
                  {collapsed ? (
                    item.badge ? <span className="absolute top-1.5 end-1.5 size-1.5 rounded-full bg-aegold-500" aria-hidden /> : null
                  ) : (
                    <>
                      {/* A menu name is never cut (visual item 8): a long one wraps to a second line. */}
                      <span className="min-w-0 leading-snug">{item.label}</span>
                      {item.badge ? <span className="ms-auto">{item.badge}</span> : null}
                    </>
                  )}
                </a>
              );
              return (
                <li key={item.key}>
                  {collapsed ? (
                    <Tooltip content={item.label} side={tipSide}>
                      {link}
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {footer || onToggleCollapsed ? (
        <div className={cn("mt-auto flex flex-col gap-2 px-3", collapsed && "items-center")}>
          {footer}
          {onToggleCollapsed ? (
            collapsed ? (
              <Tooltip content={toggleLabel} side={tipSide}>
                <Button variant="ghost" size="icon-sm" onClick={onToggleCollapsed} aria-expanded={false} aria-label={toggleLabel}>
                  <PanelLeftOpen className="rtl:rotate-180" />
                </Button>
              </Tooltip>
            ) : (
              <Button variant="ghost" size="sm" onClick={onToggleCollapsed} aria-expanded aria-label={toggleLabel} className="w-full justify-start gap-3">
                <PanelLeftClose className="rtl:rotate-180" />
                <span className="truncate">{toggleLabel}</span>
              </Button>
            )
          ) : null}
        </div>
      ) : null}
    </nav>
  );
}

// ---------------------------------------------------------------- App shell (sidebar layout)
export function AppShell({
  header,
  sidebar,
  mobileSidebar,
  children,
  sidebarWidth = "w-64",
  collapsed = false,
  className,
  mainClassName,
  fullWidth = false,
}: {
  header: ReactNode;
  sidebar: ReactNode;
  /** Rendered inside the mobile drawer instead of `sidebar` (e.g. an always-expanded nav when the desktop one is collapsed). */
  mobileSidebar?: ReactNode;
  children: ReactNode;
  sidebarWidth?: string;
  collapsed?: boolean;
  className?: string;
  /** Replaces the default page padding of `<main>` (chat-style surfaces render edge to edge and manage their own scrolling). */
  mainClassName?: string;
  /** Lets the shell span the whole viewport instead of the 1440px reading column. */
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <NavDrawerContext.Provider value={{ open, toggle: () => setOpen((o) => !o) }}>
      <div className={cn("min-h-dvh flex flex-col", className)}>
        {header}
        <div className={cn("mx-auto flex w-full flex-1", fullWidth ? "max-w-none" : "max-w-[1440px]")}>
          {/* `overflow-clip` (not `overflow-hidden`) so the aside never becomes a scroll container, which would break the sticky inner div. */}
          <aside data-print="hide" className={cn("hidden shrink-0 overflow-clip border-e border-sand-200/80 bg-white/40 transition-[width] duration-200 ease-out lg:block", collapsed ? "w-16" : sidebarWidth)}>
            <div className="sticky top-[68px] h-[calc(100dvh-68px)]">{sidebar}</div>
          </aside>
          {open ? (
            <div className="lg:hidden fixed inset-0 z-30 bg-aeblack-950/30" onClick={() => setOpen(false)}>
              {/* Below the header, whose button closes it again; choosing a page closes it too. */}
              <aside className="absolute bottom-0 start-0 top-[68px] w-72 border-e border-sand-200 bg-card animate-in slide-in-from-left rtl:slide-in-from-right" onClick={(e) => { e.stopPropagation(); if ((e.target as HTMLElement).closest("a")) setOpen(false); }}>
                <div className="h-full">{mobileSidebar ?? sidebar}</div>
              </aside>
            </div>
          ) : null}
          <main id="main" className={cn("min-w-0 flex-1", mainClassName ?? "px-4 py-6 sm:px-6 lg:px-8")}>
            {children}
          </main>
        </div>
      </div>
    </NavDrawerContext.Provider>
  );
}

// ---------------------------------------------------------------- Page header
export interface Crumb {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
}

export function PageHeader({ title, description, crumbs, actions, eyebrow, className, meta }: { title: ReactNode; description?: ReactNode; crumbs?: Crumb[]; actions?: ReactNode; eyebrow?: ReactNode; className?: string; meta?: ReactNode }) {
  return (
    <div className={cn("mb-6 flex flex-col gap-3", className)}>
      {crumbs && crumbs.length > 0 ? (
        <nav aria-label={microcopy("breadcrumb")} className="flex items-center gap-1 text-xs text-muted-foreground">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 ? <ChevronRight className="size-3 rtl:rotate-180" /> : null}
              {c.href || c.onClick ? (
                <a
                  href={c.href ?? "#"}
                  onClick={(e) => {
                    if (c.onClick) {
                      e.preventDefault();
                      c.onClick();
                    }
                  }}
                  className="hover:text-aegold-800 transition-colors"
                >
                  {c.label}
                </a>
              ) : (
                <span className="text-foreground">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow ? <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-aegold-700">{eyebrow}</p> : null}
          <motion.h1 className="text-2xl font-semibold sm:text-3xl" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {title}
          </motion.h1>
          {description ? <p className="mt-1.5 max-w-3xl text-sm text-muted-foreground sm:text-base">{description}</p> : null}
          {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Footer
export function GovFooter({ children, links, className }: { children: ReactNode; links?: Array<{ label: ReactNode; href: string }>; className?: string }) {
  return (
    <footer className={cn("mt-auto border-t border-sand-200 bg-sadu", className)}>
      <div className="mx-auto flex max-w-[1440px] flex-col gap-3 px-4 py-6 text-xs text-sand-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <Emblem size={28} />
          <span>{children}</span>
        </div>
        {links ? (
          <ul className="flex flex-wrap gap-4">
            {links.map((l, i) => (
              <li key={i}>
                <a href={l.href} className="hover:text-aegold-800 transition-colors">{l.label}</a>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------- Collapsible section
/** A page section that shows its heading only until opened, so long pages read as a list of headings. */
export function CollapsibleSection({ title, meta, actions, defaultOpen = false, open: controlled, onOpenChange, children, className, contentClassName }: {
  title: ReactNode; meta?: ReactNode; actions?: ReactNode; defaultOpen?: boolean; open?: boolean; onOpenChange?: (open: boolean) => void; children: ReactNode; className?: string; contentClassName?: string;
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = controlled ?? uncontrolled;
  const toggle = () => { const next = !open; setUncontrolled(next); onOpenChange?.(next); };
  return (
    <section className={cn("min-w-0 rounded-2xl border border-sand-200 bg-card", className)}>
      <div className="flex items-center gap-3 px-5 py-3.5">
        <button type="button" aria-expanded={open} onClick={toggle} className="flex min-w-0 flex-1 items-center gap-2 text-start">
          <ChevronDown className={cn("size-4 shrink-0 text-sand-600 transition-transform", !open && "-rotate-90 rtl:rotate-90")} />
          <span className="truncate font-heading text-base font-semibold text-aeblack-950">{title}</span>
          {meta ? <span className="shrink-0 text-xs text-muted-foreground">{meta}</span> : null}
        </button>
        {actions}
      </div>
      {open ? <div className={cn("border-t border-sand-200 p-5", contentClassName)}>{children}</div> : null}
    </section>
  );
}

/** A small info mark whose tooltip carries the explanation that used to sit under a heading. */
export function InfoHint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Tooltip content={children}>
      <button type="button" aria-label={typeof children === "string" ? children : undefined} className={cn("inline-grid size-5 shrink-0 place-items-center rounded-full text-sand-500 hover:text-aegold-700", className)}>
        <Info className="size-3.5" />
      </button>
    </Tooltip>
  );
}
